// Shared copy-to-clipboard behaviour with a floating confirmation toast.
//
// Used by the tests datatable (click a cell to copy its ID) and by the
// Subscription section of a business (copy Stripe customer / subscription ids).
//
// The toast is appended to document.body so it can never be clipped by a table
// cell or a scrolling container. That also means scoped component styles cannot
// reach it, so `.copy-notification` lives in public/style.css rather than in any
// single component.

const TOAST_VISIBLE_MS = 1500;
const TOAST_FADE_MS = 300;

export default {
  methods: {
    // `event` is optional: when given, the toast is positioned over the clicked
    // element, otherwise it falls back to the top centre of the viewport.
    copyToClipboard(text, event, fieldName) {
      const value = text === null || text === undefined ? "" : String(text);
      if (!value) {
        return;
      }

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard
          .writeText(value)
          .then(() => {
            this.showCopyNotification(fieldName, value, event);
          })
          .catch((err) => {
            console.error("Failed to copy text: ", err);
            this.fallbackCopyToClipboard(value, fieldName, event);
          });
      } else {
        // Older browsers, or a non-HTTPS origin where the Clipboard API is blocked.
        this.fallbackCopyToClipboard(value, fieldName, event);
      }
    },
    fallbackCopyToClipboard(text, fieldName, event) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy");
        this.showCopyNotification(fieldName, text, event);
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }

      document.body.removeChild(textArea);
    },
    showCopyNotification(fieldName, text, event) {
      const notification = document.createElement("div");
      notification.className = "copy-notification";

      const content = document.createElement("div");
      content.className = "copy-notification-content";
      content.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">' +
        '<path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>' +
        "</svg>";

      // Set the label as text rather than markup so a field name can never be
      // interpreted as HTML.
      const label = document.createElement("span");
      label.textContent = `${fieldName} copied!`;
      content.appendChild(label);
      notification.appendChild(content);

      document.body.appendChild(notification);

      const anchor = event && event.target ? event.target.closest("[data-copy-anchor], .copyable-cell") : null;
      notification.style.position = "fixed";
      if (anchor) {
        const rect = anchor.getBoundingClientRect();
        if (anchor.getAttribute("data-copy-placement") === "beside") {
          // Sit immediately to the right of the trigger with a small gap,
          // vertically centred on it. Used where the trigger is a small icon
          // button rather than a wide cell.
          const gap = 8;
          notification.style.left = `${rect.right + gap}px`;
          notification.style.top = `${rect.top + rect.height / 2 - notification.offsetHeight / 2}px`;
        } else {
          // Default: top of the anchor, horizontally centred over it.
          notification.style.top = `${rect.top}px`;
          notification.style.left = `${rect.left + rect.width / 2}px`;
        }
      } else {
        notification.style.top = "20px";
        notification.style.left = "50%";
      }
      notification.style.transform = "translateY(-10px)";

      // Let the element land in the DOM before transitioning it in.
      setTimeout(() => {
        notification.classList.add("show");
      }, 10);

      setTimeout(() => {
        notification.classList.remove("show");
        setTimeout(() => {
          if (notification.parentNode) {
            document.body.removeChild(notification);
          }
        }, TOAST_FADE_MS);
      }, TOAST_VISIBLE_MS);
    },
  },
};
